import { useEffect, useState } from 'react';

import { format, isAfter, isBefore, isValid, isWithinInterval } from 'date-fns';
import Decimal from 'decimal.js';
import { cloneDeep, differenceBy, orderBy, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  OrganizationalUnit,
  OrganizationalUnitDistribution,
  PlanUnit,
  SharedOrganizationalUnitExt,
  WorkAgreementDetails
} from 'components/types';

import { getReferenceDate } from '../../../utils';
import { getDistributionTitle } from './utils';
import { formatByLocale, fromISOString } from 'utils/date';
import { displayNumber } from 'utils/misc';

import { createOrganizationalUnitDistribution } from 'RestAPIs/employments/organizational-unit-distributions';

import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';

import {
  DatePicker,
  OptionalDatePicker,
  showSnackbar
} from 'components/shared';

import SharedOrganizationalUnitEntry from './SharedOrganizationalUnit';
import {
  GridCompact12,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import { Icon } from '@sdb/planning-ui';

type UpdateProps = {
  unit: SharedOrganizationalUnitExt;
  isAdditional?: boolean;
  props:
    | {
        organizationalUnit: OrganizationalUnit;
      }
    | {
        hours?: number | null;
        percentage: number;
      };
};

type Props = OrganizationalUnitDistribution & {
  workAgreementId: number;
  employmentHours?: number;
  canEdit: boolean;
  planUnits: PlanUnit[];
  allDistributions: OrganizationalUnitDistribution[];
  workAgreements: WorkAgreementDetails[];
  loadOrganizationalUnitDistributions: () => void;
};

export default function OrganizationalUnitDistributionEntry(props: Props) {
  const { t } = useTranslation('common');
  const {
    id,
    workAgreementId,
    startDate: initialStartDate,
    endDate: initialEndDate,
    canEdit,
    planUnits,
    allDistributions,
    workAgreements,
    loadOrganizationalUnitDistributions
  } = props;

  const [startDate, setStartDate] = useState<Date | null>(
    new Date(initialStartDate)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    initialEndDate ? new Date(initialEndDate) : null
  );
  const [employmentHours, setEmploymentHours] = useState<number | undefined>();
  const [sharedOrganizationalUnits, setSharedOrganizationalUnits] = useState<
    SharedOrganizationalUnitExt[]
  >(props.sharedOrganizationalUnits);
  const [
    additionalSharedOrganizationalUnits,
    setAdditionalSharedOrganizationalUnits
  ] = useState<SharedOrganizationalUnitExt[]>([]);
  const [isEditMode, setInEditMode] = useState<boolean>(false);
  const [isDateOverlapping, setIsDateOverlapping] = useState<boolean>(false);
  const [isEndDateChanged, setIsEndDateChanged] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const initialStart = new Date(initialStartDate);
    const initialEnd = initialEndDate ? new Date(initialEndDate) : null;
    const isEndDateRemoved = initialEndDate && !endDate;
    const isStartDateEarlier = startDate
      ? isBefore(startDate, initialStart)
      : false;
    const isEndDateLater =
      initialEnd && endDate ? isAfter(endDate, initialEnd) : false;

    setIsDateOverlapping(
      isEndDateRemoved || isEndDateLater || isStartDateEarlier
    );
    setIsEndDateChanged(initialEnd !== endDate);
    updateHoursBasedOnInterval(startDate, endDate);
  }, [startDate, endDate]);

  const diffHours = employmentHours
    ? Decimal.sub(
        employmentHours,
        Decimal.add(
          additionalSharedOrganizationalUnits.reduce(
            (cnt, o) => Decimal.add(cnt, o.hours || 0).toNumber(),
            0
          ),
          sharedOrganizationalUnits.reduce(
            (cnt, o) => Decimal.add(cnt, o.hours || 0).toNumber(),
            0
          )
        ).toNumber()
      ).toNumber()
    : null;

  const diffPercentage = Decimal.sub(
    100,
    Decimal.add(
      Decimal.mul(
        additionalSharedOrganizationalUnits.reduce(
          (cnt, o) => Decimal.add(cnt, o.percentage || 0).toNumber(),
          0
        ),
        100
      ).toNumber(),
      Decimal.mul(
        sharedOrganizationalUnits.reduce(
          (cnt, o) => Decimal.add(cnt, o.percentage || 0).toNumber(),
          0
        ),
        100
      ).toNumber()
    ).toNumber()
  ).toNumber();

  const updateHoursBasedOnInterval = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    const employmentHours = workAgreements
      .find((w) => w.waId === workAgreementId)
      ?.benefits.find(
        (b) =>
          isWithinInterval(getReferenceDate(startDate, endDate), {
            start: fromISOString(b.validFrom),
            end: fromISOString(b.validTo)
          }) && !b.flags.includes('INVALID')
      )?.weekHoursMin;
    setEmploymentHours(employmentHours);
    setSharedOrganizationalUnits(
      sharedOrganizationalUnits.map((u) => ({
        ...u,
        hours:
          u.percentage && employmentHours
            ? Decimal.mul(u.percentage, employmentHours).toNumber()
            : null
      }))
    );
    setAdditionalSharedOrganizationalUnits(
      additionalSharedOrganizationalUnits.map((u) => ({
        ...u,
        hours:
          u.percentage && employmentHours
            ? Decimal.mul(u.percentage, employmentHours).toNumber()
            : null
      }))
    );
  };

  const onCancelEdit = () => {
    const initStartDate = new Date(initialStartDate);
    const initEndDate = initialEndDate ? new Date(initialEndDate) : null;

    setStartDate(initStartDate);
    updateHoursBasedOnInterval(initStartDate, initEndDate);
    setEndDate(initEndDate);
    setSharedOrganizationalUnits(props.sharedOrganizationalUnits);
    setAdditionalSharedOrganizationalUnits([]);
    setInEditMode(false);
    setHasChanges(false);
    setIsDateOverlapping(false);
  };

  const updateSharedOrganizationalUnit = ({
    props,
    unit,
    isAdditional
  }: UpdateProps) => {
    const units = isAdditional
      ? additionalSharedOrganizationalUnits
      : sharedOrganizationalUnits;
    const newUnits = [
      ...units.filter((d) => d.id !== unit.id),
      {
        ...units.filter((d) => d.id === unit.id)[0],
        ...props
      }
    ];

    setHasChanges(true);
    if (isAdditional) {
      setAdditionalSharedOrganizationalUnits(newUnits);
    } else {
      setSharedOrganizationalUnits(newUnits);
    }
  };

  const deleteSharedOrganizationalUnit = (
    unit: SharedOrganizationalUnitExt,
    isAdditional?: boolean
  ) => {
    setHasChanges(true);
    if (isAdditional) {
      setAdditionalSharedOrganizationalUnits(
        additionalSharedOrganizationalUnits.filter((o) => o.id !== unit.id)
      );
    } else {
      setSharedOrganizationalUnits(
        sharedOrganizationalUnits.filter((o) => o.id !== unit.id)
      );
    }
  };

  const onSave = () => {
    if (!startDate) {
      return;
    }

    if (endDate && isAfter(startDate, endDate)) {
      showSnackbar({
        message: t('planUnitDistribution.warningStartDateExceedsEndDate'),
        severity: 'error'
      });
      return;
    }

    const data = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
      sharedOrganizationalUnits: additionalSharedOrganizationalUnits
        .concat(sharedOrganizationalUnits)
        .map(({ organizationalUnit, percentage }) =>
          organizationalUnit && percentage !== undefined
            ? {
                organizationalUnitId: organizationalUnit.id,
                percentage
              }
            : null
        )
        .flatMap((a) => (a ? [a] : []))
    };

    createOrganizationalUnitDistribution({
      employmentId: workAgreementId,
      data
    }).then(
      () => {
        setInEditMode(false);
        setHasChanges(false);
        showSnackbar({ message: t('changesSaved'), severity: 'success' });
        loadOrganizationalUnitDistributions();
      },
      () => {
        showSnackbar({
          message: t('changesFailed'),
          severity: 'error'
        });
      }
    );
  };

  const addSharedOrganizationalUnit = () => {
    setAdditionalSharedOrganizationalUnits([
      ...additionalSharedOrganizationalUnits,
      {
        hours: 0,
        percentage: 0,
        isBlocked: false,
        id: uuidv4(),
        order:
          (sortBy(additionalSharedOrganizationalUnits, 'order').pop()?.order ||
            0) + 1,
        isNew: true
      }
    ]);
  };

  const cloneDistribution = (distribution: OrganizationalUnitDistribution) => {
    setSharedOrganizationalUnits(
      cloneDeep(distribution.sharedOrganizationalUnits)
    );
    setAdditionalSharedOrganizationalUnits([]);
    setHasChanges(true);
    setAnchorEl(null);
  };

  const prevDistributions = allDistributions.filter(
    (d) => d.sharedOrganizationalUnits.length && d.id !== id
  );

  return (
    <div className="distribution-entry d-flex v-top">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            {employmentHours && (
              <TableCell className="text-center pr26 relative">
                {t('hours')}
                <Tooltip
                  title={
                    t('planUnitDistribution.hoursBasedOnStartDate', {
                      startDate: formatByLocale(
                        getReferenceDate(startDate, endDate),
                        'PP'
                      )
                    }) || ''
                  }
                  arrow
                >
                  <Icon
                    type="circle-info"
                    fontSize="small"
                    className="info-hours"
                  />
                </Tooltip>
              </TableCell>
            )}
            <TableCell className="text-center pr26">%</TableCell>
          </TableRow>
          <TableRow className="source-row">
            <TableCell>{t('planUnitDistribution.sourcePlanUnit')}</TableCell>
            {diffHours !== null && (
              <TableCell className="text-center pr26 fs17">
                {displayNumber(diffHours)}
              </TableCell>
            )}
            <TableCell className="text-center pr26 fs17">
              {displayNumber(diffPercentage)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              colSpan={employmentHours ? 3 : 2}
              className="additional-cell"
            >
              <div className="d-flex">
                <span className="text">
                  {t('planUnitDistribution.additionalPlanUnits')}
                </span>
                <div className="actions-cell">
                  <div className="actions d-flex">
                    {isEditMode && diffPercentage > 0 && (
                      <IconButton
                        onClick={addSharedOrganizationalUnit}
                        size="small"
                        color="primary"
                      >
                        <Icon type="plus" fontSize="small" />
                      </IconButton>
                    )}
                    {isEditMode && prevDistributions.length > 0 && (
                      <Tooltip
                        arrow
                        title={
                          t('planUnitDistribution.reuseDistribution') || ''
                        }
                      >
                        <IconButton
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                          }}
                          color="primary"
                          className="clone-btn"
                          size="large"
                        >
                          <Icon type="clone" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                    >
                      {prevDistributions.map((distribution) => (
                        <MenuItem
                          onClick={() => cloneDistribution(distribution)}
                          key={distribution.id}
                        >
                          {getDistributionTitle(distribution)}
                        </MenuItem>
                      ))}
                    </Menu>
                  </div>
                </div>
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderBy(additionalSharedOrganizationalUnits, 'order', 'desc').map(
            (unit) => (
              <SharedOrganizationalUnitEntry
                key={unit.id}
                unit={unit}
                isEditMode={isEditMode}
                diffHours={diffHours}
                diffPercentage={diffPercentage}
                employmentHours={employmentHours}
                planUnits={differenceBy(
                  planUnits,
                  additionalSharedOrganizationalUnits
                    .filter((o) => o.organizationalUnit)
                    .concat(sharedOrganizationalUnits)
                    .filter((o) =>
                      unit.organizationalUnit
                        ? o.organizationalUnit?.id !==
                          unit.organizationalUnit.id
                        : true
                    )
                    .map((o) => o.organizationalUnit),
                  'id'
                )}
                onUpdate={(props) => {
                  updateSharedOrganizationalUnit({
                    props,
                    unit,
                    isAdditional: true
                  });
                }}
                onDelete={() => {
                  deleteSharedOrganizationalUnit(unit, true);
                }}
              />
            )
          )}
          {sortBy(sharedOrganizationalUnits, 'organizationalUnit.name').map(
            (unit) => (
              <SharedOrganizationalUnitEntry
                key={unit.id}
                unit={unit}
                isEditMode={isEditMode}
                diffHours={diffHours}
                diffPercentage={diffPercentage}
                employmentHours={employmentHours}
                planUnits={planUnits}
                onUpdate={(props) => {
                  updateSharedOrganizationalUnit({ props, unit });
                }}
                onDelete={() => {
                  deleteSharedOrganizationalUnit(unit);
                }}
              />
            )
          )}
        </TableBody>
      </Table>
      <GridContainer className="details">
        {isEditMode && (
          <GridCompact12>
            <h3>{t('planUnitDistribution.whichPeriodDoYouWantToUpdate')}</h3>
          </GridCompact12>
        )}
        <GridCompact6>
          <DatePicker
            date={startDate}
            disabled={!isEditMode}
            onChange={(date) => {
              if (isValid(date)) {
                setStartDate(date);
                updateHoursBasedOnInterval(date, endDate);
                setHasChanges(true);
              }
            }}
            label={t('startDate')}
            readOnly={!isEditMode}
          />
        </GridCompact6>
        <GridCompact6 className="d-flex v-bottom end">
          <OptionalDatePicker
            initialDate={endDate}
            disabled={!isEditMode}
            onDateChange={(date) => {
              setEndDate(date);

              if (isValid(startDate) && isValid(endDate)) {
                updateHoursBasedOnInterval(startDate, date);
              }

              setHasChanges(true);
            }}
          />
        </GridCompact6>
        <GridCompact12>
          {isDateOverlapping && (
            <Box mb={3}>
              <Typography variant="body1">
                {t('planUnitDistribution.warningOverlappingDate')}
              </Typography>
            </Box>
          )}
          {isEndDateChanged && (
            <Box mb={3}>
              <Typography variant="body1">
                {t('planUnitDistribution.warningEndDateChanged')}
              </Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="space-between">
            {isEditMode && (
              <>
                <Button
                  variant="contained"
                  disabled={
                    !hasChanges ||
                    !startDate ||
                    !additionalSharedOrganizationalUnits.every(
                      (unit) =>
                        !Number.isNaN(unit.percentage) &&
                        unit.organizationalUnit
                    )
                  }
                  onClick={onSave}
                >
                  {t('save')}
                </Button>
                <Button variant="outlined" onClick={onCancelEdit}>
                  {t('cancel')}
                </Button>
              </>
            )}
            {canEdit && !isEditMode && (
              <Button variant="outlined" onClick={() => setInEditMode(true)}>
                {t('edit')}
              </Button>
            )}
          </Box>
        </GridCompact12>
      </GridContainer>
    </div>
  );
}
